import { Box, ButtonBase, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/pro-light-svg-icons"
import { faSparkles } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import { dishSummary } from "../helpers"
import { DishFormValues } from "../menuTemplatesTypes"
import { DishCardPhoto } from "./DishCardPhoto"

const useStyles = makeStyles()((theme) => ({
    root: {
        position: "relative",
    },
    card: {
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        width: "100%",
        height: "100%",
        minHeight: "9.75rem",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".5rem",
        background: theme.palette.background.paper,
        overflow: "hidden",
        textAlign: "left",
        transition: theme.transitions.create(["border-color", "box-shadow"], {
            duration: theme.transitions.duration.short,
        }),
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
    cardError: {
        borderColor: theme.palette.error.main,
    },
    imageWrap: {
        width: "100%",
    },
    body: {
        padding: ".5rem .75rem",
        width: "100%",
    },
    name: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    meta: {
        fontSize: ".6875rem",
        color: theme.palette.tailwind.slate[500],
    },
    aiOff: {
        fontSize: ".75rem",
        fontWeight: 700,
        color: theme.palette.tailwind.violet[600],
    },
    aiIcon: {
        fontSize: ".75rem",
        marginRight: ".25rem",
    },
    removeButton: {
        position: "absolute",
        top: ".375rem",
        right: ".375rem",
        width: "1.5rem",
        height: "1.5rem",
        background: theme.palette.background.paper,
        color: theme.palette.tailwind.slate[500],
        "& svg": {
            fontSize: ".875rem",
        },
        "&:hover": {
            background: theme.palette.background.paper,
            color: theme.palette.error.main,
        },
    },
}))

type Props = Readonly<{
    dish: DishFormValues
    hasError: boolean
    onOpen: () => void
    onRemove: () => void
}>

export function DishCard({ dish, hasError, onOpen, onRemove }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    const summary = dishSummary(dish, l10n)

    return (
        <Box className={classes.root}>
            <ButtonBase className={cx(classes.card, hasError && classes.cardError)} onClick={onOpen}>
                <Box className={classes.imageWrap}>
                    <DishCardPhoto photoUrl={dish.image.photo?.url} />
                </Box>
                <Stack className={classes.body} alignItems="flex-start" spacing=".25rem">
                    {dish.name && (
                        <Typography variant="subtitle2" className={classes.name}>
                            {dish.name}
                        </Typography>
                    )}
                    <Typography variant="caption" className={classes.meta}>
                        {summary.mandatoryLine}
                    </Typography>
                    {summary.aiOffLine && (
                        <Typography variant="caption" className={classes.aiOff}>
                            <FontAwesomeIcon icon={faSparkles} className={classes.aiIcon} />
                            {summary.aiOffLine}
                        </Typography>
                    )}
                </Stack>
            </ButtonBase>
            <LocalizedStrict id="merchant-menu-templates-dish-remove-button" attrs={{ "aria-label": true }}>
                <IconButton size="small" className={classes.removeButton} aria-label="Remove dish" onClick={onRemove}>
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </LocalizedStrict>
        </Box>
    )
}
